import { useEffect, useId, useState } from 'react';
import { Option as OptionType, OptionValueType, Output } from './types';
import { translations } from '../translations/translations';
import triggerOptionFieldRegistry from './optionFields/triggerOptionFieldRegistry';
import classNames from 'classnames';
import { ExIcon } from '@boomi/exosphere';

interface Props {
    option: OptionType;
    value: OptionValueType;
    onChange: (id: string, value: OptionValueType) => void;
    outputs?: Output[];
    showValidation: boolean;
    language?: string;
}

const Option = ({ option, value, onChange, outputs, showValidation, language = 'en' }: Props) => {
    const [validationMessage, setValidationMessage] = useState<string | null>(null);
    const id = useId();

    useEffect(() => {
        const getValidationMessage = (value: OptionValueType) => {
            if (option.isRequired && !value) {
                return translations[language].COMMON_field_required;
            }

            if (
                option.type === 'PhoneNumber' &&
                typeof value === 'string' &&
                !/^\+\d{1,2} \(\d{3}\) \d{3}-\d{4}$/.test(value)
            ) {
                return translations[language].TASK_validation_phone_number;
            }

            return null;
        };

        setValidationMessage(getValidationMessage(value));
    }, [value, language, option.isRequired, option.type]);

    const Input = triggerOptionFieldRegistry[option.type].component;

    // Figure out if we need to use a custom input and thus our own label + messages.
    const optionUsesCustomInput = (option: OptionType) => {
        // These option types will always use a custom input type.
        const customComponentOptions = [
            'SingleSelect',
            'MultiSelect',
            'SingleSelectButtons',
            'MultiSelectButtons',
        ];
        const usesInputWithTokens = !!option.allowOutputs;
        const usesTimeOffset = option.id === 'timeoffset';
        const usesOtherCustomComponent = customComponentOptions.includes(option.type);

        return (
            (usesInputWithTokens || usesTimeOffset || usesOtherCustomComponent) &&
            option.type !== 'Boolean'
        );
    };

    const formFieldClasses = classNames('form-field', {
        'is-not-valid': showValidation && validationMessage && optionUsesCustomInput(option),
    });

    const labelClasses = classNames('label xxs', {
        required: option.isRequired,
    });

    return (
        <div className={formFieldClasses}>
            {optionUsesCustomInput(option) && (
                <label className={labelClasses} htmlFor={id + option.id}>
                    {option.label}
                </label>
            )}
            <Input
                id={id}
                option={option}
                outputs={outputs}
                value={value}
                onChange={onChange}
                type={triggerOptionFieldRegistry[option.type].type}
                autoComplete={triggerOptionFieldRegistry[option.type].autoComplete}
                showValidation={showValidation}
                validationMessage={validationMessage}
            />
            {optionUsesCustomInput(option) && (
                <section className="messages">
                    {showValidation && validationMessage && (
                        <p className="validation-message text xxs">
                            <ExIcon
                                className="validation-message-icon"
                                icon="Error"
                                hide-browser-tooltip
                            />
                            {validationMessage}
                        </p>
                    )}
                    {/* Exosphere hides help text if there is a validation error, so we'll match this behavior. */}
                    {!showValidation && !validationMessage && option.help && (
                        <p className="help-message text xxs">{option.help}</p>
                    )}
                </section>
            )}
        </div>
    );
};

export default Option;
